import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Download } from 'lucide-react';
import { Button } from '~/components/ui/buttons/Button';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { useImportPlatformProducts } from '~/api/platformProductImport';
import {
  PlatformProductImportAction,
  type PlatformProductImportInput,
} from '~/graphql/graphql';
import {
  ACCEPTED_EXTENSIONS,
  parseFile,
} from '~/pages/inventory-import/lib/parseFile';
import {
  Changes,
  FileErrors,
  RowErrors,
  type ImportResult,
} from './ImportResultTables';
import { generateCsvTemplate } from '~/utils';

const NAMESPACE = 'product-import';

// Matches ProductImportValidator.REQUIRED_HEADERS on the backend.
const REQUIRED_HEADERS = [
  'code',
  'name',
  'category',
  'standard_units_code',
  'nominal_thickness_inches',
  'nominal_width_inches',
  'actual_thickness_inches',
  'actual_width_inches',
  'pieces_per_bundle',
  'lengths_inches',
];
const TEMPLATE_HEADERS = [...REQUIRED_HEADERS, "capacity:48' Flatbed"];

type Stage =
  | { kind: 'upload' }
  | { kind: 'preview'; input: PlatformProductImportInput; result: ImportResult }
  | { kind: 'applied'; result: ImportResult };

const hasErrors = (result: ImportResult): boolean =>
  result.fileErrors.length > 0 || result.rowErrors.length > 0;

const countActions = (
  result: ImportResult,
  action: PlatformProductImportAction
): number =>
  result.products.filter((product) => product.action === action).length;

const nothingToApply = (result: ImportResult): boolean =>
  hasErrors(result) ||
  countActions(result, PlatformProductImportAction.Unchanged) ===
    result.products.length;

export default function AdminProductImport() {
  const { t } = useTranslation([NAMESPACE, 'inventory-import']);
  const inputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'upload' });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const { mutate, isPending } = useImportPlatformProducts();
  const busy = isPending || parsing;

  const onError = () => toast.error(t('toast.error'));

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploadError(null);
    setParsing(true);
    try {
      const parsed = await parseFile(file);
      const input: PlatformProductImportInput = {
        fileName: parsed.fileName,
        headers: parsed.headers.map(
          (header) => parsed.renamedHeaders[header] ?? header
        ),
        rows: parsed.rows.map((row) =>
          parsed.headers.map((header) => row[header] ?? '')
        ),
        rowNumbers: parsed.rowNumbers,
      };
      mutate(
        { input, dryRun: true },
        {
          onSuccess: (data) =>
            setStage({
              kind: 'preview',
              input,
              result: data.importPlatformProducts,
            }),
          onError,
        }
      );
    } catch (error) {
      const parseError = t('upload.error_parse', { ns: 'inventory-import' });
      setUploadError(
        error instanceof Error
          ? t(error.message, {
              ns: 'inventory-import',
              defaultValue: parseError,
            })
          : parseError
      );
    } finally {
      setParsing(false);
    }
  };

  const handleApply = (input: PlatformProductImportInput) =>
    mutate(
      { input, dryRun: false },
      {
        onSuccess: (data) => {
          const result = data.importPlatformProducts;
          if (hasErrors(result)) {
            setStage({ kind: 'preview', input, result });
            return;
          }
          setStage({ kind: 'applied', result });
          toast.success(t('toast.applied'));
        },
        onError,
      }
    );

  const startOver = () => setStage({ kind: 'upload' });

  return (
    <div className="w-full flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-neut-prim">{t('title')}</h1>
        <p className="text-sm text-neut-sec mt-1">{t('subtitle')}</p>
      </div>

      {stage.kind === 'upload' && (
        <SectionPanel title={t('upload.title')}>
          <div className="flex flex-col gap-4">
            <ul className="list-disc pl-5 text-sm text-neut-sec">
              <li>
                {t('upload.requiredHeaders', {
                  columns: REQUIRED_HEADERS.join(', '),
                })}
              </li>
              <li>{t('upload.rules.capacity')}</li>
              <li>{t('upload.rules.capacityUnit')}</li>
              <li>{t('upload.rules.lengths')}</li>
              <li>{t('upload.rules.existing')}</li>
            </ul>
            <div className="flex items-center gap-3">
              <Button
                width="auto"
                loading={busy}
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                data-testid="product-import-choose"
              >
                {t('upload.choose')}
              </Button>
              <Button
                variant="secondary"
                width="auto"
                onClick={() =>
                  generateCsvTemplate(
                    TEMPLATE_HEADERS,
                    'product_import_template.csv'
                  )
                }
                data-testid="product-import-template"
              >
                <Download size={16} />
                {t('upload.template')}
              </Button>
              <span className="text-xs text-neut-sec">
                {t('upload.supported', {
                  types: ACCEPTED_EXTENSIONS.join(', '),
                })}
              </span>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              className="hidden"
              data-testid="product-import-file"
              onChange={(event) => {
                void handleFile(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
            {uploadError && (
              <p
                data-testid="product-import-upload-error"
                className="rounded-md bg-error-100 px-3 py-2 text-sm text-error-600"
              >
                {uploadError}
              </p>
            )}
          </div>
        </SectionPanel>
      )}

      {stage.kind === 'preview' && (
        <>
          <SectionPanel
            title={t('preview.title', { fileName: stage.input.fileName })}
            aside={
              <Button
                variant="secondary"
                size="sm"
                width="auto"
                onClick={startOver}
                disabled={isPending}
                data-testid="product-import-start-over"
              >
                {t('startOver')}
              </Button>
            }
          >
            <div className="flex flex-wrap items-center gap-3">
              {hasErrors(stage.result) && (
                <p className="text-sm text-error-600">{t('preview.blocked')}</p>
              )}
              {!hasErrors(stage.result) && nothingToApply(stage.result) && (
                <p className="text-sm text-neut-sec">
                  {t('preview.nothingToDo')}
                </p>
              )}
              <Button
                width="auto"
                loading={isPending}
                disabled={isPending || nothingToApply(stage.result)}
                onClick={() => handleApply(stage.input)}
                data-testid="product-import-apply"
              >
                {t('preview.apply')}
              </Button>
            </div>
          </SectionPanel>
          {stage.result.fileErrors.length > 0 && (
            <FileErrors errors={stage.result.fileErrors} />
          )}
          {stage.result.rowErrors.length > 0 && (
            <RowErrors errors={stage.result.rowErrors} />
          )}
          {stage.result.products.length > 0 && (
            <Changes products={stage.result.products} />
          )}
        </>
      )}

      {stage.kind === 'applied' && (
        <SectionPanel
          title={t('summary.title')}
          aside={
            <Button
              variant="secondary"
              size="sm"
              width="auto"
              onClick={startOver}
              data-testid="product-import-start-over"
            >
              {t('startOver')}
            </Button>
          }
        >
          <p
            className="text-sm text-neut-prim"
            data-testid="product-import-summary"
          >
            {t('summary.counts', {
              created: countActions(
                stage.result,
                PlatformProductImportAction.Create
              ),
              updated: countActions(
                stage.result,
                PlatformProductImportAction.Update
              ),
              unchanged: countActions(
                stage.result,
                PlatformProductImportAction.Unchanged
              ),
            })}
          </p>
        </SectionPanel>
      )}
    </div>
  );
}
